namespace app.pesquisaAvancada {
    'use strict';
    import IScope = angular.IScope;
    import ISidenavService = angular.material.ISidenavService;
    import IDialogService = angular.material.IDialogService;
    import Properties = app.support.constants.Properties;
    import MessagesService = app.support.messaging.MessagesService;
    import CommandService = app.support.command.CommandService;
    import ISCEService = angular.ISCEService;
    import IHttpService = angular.IHttpService;
    import ITranslateService = angular.translate.ITranslateService;
    
    enum Tab {
        NEW_SEARCH, RESULT, EDIT_SEARCH 
    }

    export class PesquisaAvancadaController {

        public defaultSearch: ISearch;
        public newSearch: ISearch;
        public search: ISearch;
        public selectedTab: number = Tab.NEW_SEARCH;
        public fabIconElement: string;
        public fabActive: boolean = false;
        public searchData: Object = {};
        public searchComplete: boolean = false;
        public editEnabled: boolean = false;
        public tableOptions: Object;
        public tableColumnDefs: Array<Object>;
        public searchResults: Array<any> = [];
        public savedSearchs: ISearch[] = [];
        public resultTemplate: string;
        public resultActionsTemplate: string[] = [];
        private activeSaveAction: boolean = false;
        
        /** @ngInject **/
        constructor(private $rootScope: IScope,
                    private $scope: IScope,
                    private $mdDialog: IDialogService,
                    private $mdSidenav: ISidenavService,
                    private $sce: ISCEService,
                    private $http: IHttpService,
                    private $translate: ITranslateService,
                    private DTColumnDefBuilder: any,
                    private pesquisaAvancadaService: PesquisaAvancadaService,
                    private properties: Properties,
                    private commandService: CommandService,
                    private messagesService: MessagesService,
                    public searchConfig: ISearchConfig) {

            this.defaultSearch = <ISearch>{uuid: null, context: searchConfig.context, description: '', criteria: []};
            this.newSearch = angular.copy(this.defaultSearch);
            this.search = angular.copy(this.defaultSearch);
            this.configureDatatables();
            this.loadSavedSearchs();
            this.loadResultTemplate();
            commandService.findByIdAndContext('salvar-pesquisa-avancada', 'integracoes-modulos').then(() => this.activeSaveAction = true);

            this.watchSelectedTab();
            this.watchSearchCriteria();
            this.$rootScope.$on("ui:advancedSearch:doSearch", () => this.searchOnEnter());
        }
        
        /**
         * Executa uma pesquisa por enter em um campo de valor.
         */
        public searchOnEnter(): void {
            if (this.canSearch()) {
                this.messagesService.hideMessage();
                this.doSearch();
            } else {
                this.messagesService.error("Os campos da pesquisa precisam estar preenchidos corretamente!");
            }
        }

        /**
         * Verifica se uma pesquisa está válida
         */
        public canSearch(): boolean {
            let search : ISearch = (this.selectedTab === Tab.NEW_SEARCH ? this.newSearch : this.search);
            return (search.criteria.length > 0 && search.criteria.every(criterion => criterion.valid));
        }

        /**
         * Realiza uma pesquisa
         */
        public doSearch(): void {

            let valid: boolean = false;
            
            if (this.selectedTab === Tab.NEW_SEARCH && this.validate(this.newSearch)) {
                valid = true;
                angular.copy(this.newSearch, this.search);
                angular.copy(this.defaultSearch, this.newSearch);
            } else {
                valid = this.validate(this.search);
            }

            if (valid) {
                this.setLoadingProgress(true);
    
                this.pesquisaAvancadaService.executeSearch(this.searchConfig.api, this.search)
                    .then(result => {
                        this.searchResults = result;
                    }).catch(() => {
                        this.messagesService.error(this.$translate.instant("PESQUISA-AVANCADA.PESQUISA-NAO-EXECUTADA"));
                    }).finally(() => {
                        this.selectedTab = Tab.RESULT;
                        this.editEnabled = true;
                        this.searchComplete = true;
                        this.setLoadingProgress(false);
                        this.fabActive = this.activateActionHeader();
                    });
            }
        }

        /**
         * Salva uma pesquisa
         */
        public saveSearch(event): void {
            this.$mdDialog.show({
                clickOutsideToClose: true,
                controller: 'app.pesquisa-avancada.SaveSearchController',
                locals: {
                    search: this.search
                },
                controllerAs: 'vm',
                templateUrl: 'app/main/pesquisa-avancada/modals/save-search/save-search.html',
                parent: angular.element(document.body),
                targetEvent: event
            });

            var removeListener = this.$scope.$on('save-search:confirm', () => {
                let foundIndex: number = this.savedSearchs.findIndex(savedSearch => savedSearch.uuid === this.search.uuid)
                
                if (foundIndex > -1) {
                    angular.copy(this.search, this.savedSearchs[foundIndex]);
                } else {
                    this.savedSearchs.unshift(angular.copy(this.search));
                }
                removeListener();
            });
        }
        
        /**
         * Salva uma pesquisa
         */
        public deleteSearch(search: ISearch): void {
            this.pesquisaAvancadaService.deleteSearch(search)
                .then(response => {
                    if (this.search.uuid === search.uuid) this.reset();
                    let foundIndex: number = this.savedSearchs.findIndex(savedSearch => savedSearch.uuid === search.uuid);
                    this.savedSearchs.splice(foundIndex, 1);
                    this.messagesService.success("Pesquisa excluída com sucesso!");
                }).catch(() => {
                    this.messagesService.error("Erro ao excluir pesquisa!"); 
                });
        }

        /**
         * Abre a navegação das pesquisas salvas
         */
        public openSavedSearchs(): void {
            this.$mdSidenav('sidenav-search').open();
        }

        /**
         * Carrega uma pesquisa salva
         */
        public loadSearch(savedSearch: ISearch): void {
            angular.copy(savedSearch, this.search);
            this.$mdSidenav('sidenav-search').close();
            this.editEnabled = true;
            this.selectedTab = Tab.EDIT_SEARCH;
            this.searchComplete = false;
            
            if (this.search.executable) {
                this.doSearch();
            }
        }
        
        /**
         * Ativa as ações do header de pesquisa
         */
        public activateActionHeader(): boolean {
            let active = false;
            
            if (this.selectedTab === Tab.NEW_SEARCH) { 
                active = this.canSearch();
            } else if (this.selectedTab === Tab.RESULT) {
                active = this.activeSaveAction;
            } else {
                active = this.canSearch();
            }
            return active;
        }
        
        /**
         * Monta o template da tabela de resultados
         */
        private loadResultTemplate(): void {
            let template : string = "";
            this.tableColumnDefs = [];
            this.searchConfig.resultColumns.forEach((column, index) => {
                template += "<td" + ((column.result.css) ? " class='" + column.result.css + "'" : "") + ">"
                         + ((column.result.field) ? ("{{" + column.result.field + "}}") : "")
                         + ((column.result.html) ? column.result.html : "")
                         + ((column.result.actions && column.result.actions.length > 0) ? this.loadResultActionsTemplate(column.result.actions, this.resultActionsTemplate.length) : "")
                         + "</td>";
                if (column.result.type) {
                    this.tableColumnDefs.push(this.DTColumnDefBuilder.newColumnDef(index).withOption("sType", column.result.type));
                }
            });
            this.resultTemplate = this.$sce.trustAsHtml(template);
        }
        
        /**
         * Monta o template da coluna de ações se existir de resultados
         */
        private loadResultActionsTemplate(actions: IResultColumnAction[], columnActionIndex: number): string {            
            this.resultActionsTemplate[columnActionIndex] = "";
            actions.forEach(action => {
                this.commandService.findByIdAndContext(action.id, action.context)
                   .then(() => this.resultActionsTemplate[columnActionIndex] += action.html);
            });
            return "<span bind-html-compile='vm.resultActionsTemplate[" + columnActionIndex + "]'></span>";
        }
        
        /**
         * Reset da tela de pesquisa 
         */
        private reset(): void {
            angular.copy(this.defaultSearch, this.search);
            this.selectedTab = Tab.NEW_SEARCH;
            this.searchComplete = false;
            this.editEnabled = false;
        }

        /**
         * Configura o ícone do Fab button de acordo com a tab selecionada
         */
        private setFabIconElement(): void {
            const value = this.selectedTab === Tab.RESULT ? "icon-content-save" : "icon-magnify";
            this.fabIconElement = `<md-icon md-font-icon="${value}"></md-icon>`;
        }

        /**
         * Watch da tab selecionada para montar o Fab button corretamente
         */
        private watchSelectedTab(): void {
            this.$scope.$watch("vm.selectedTab", () => {
                this.setFabIconElement();
                this.fabActive = this.activateActionHeader();
            });
        }

        private watchSearchCriteria(): void {
            this.$scope.$watch('vm.newSearch', () => {
                if (this.selectedTab === Tab.NEW_SEARCH) {
                    this.fabActive = this.activateActionHeader();
                }
            }, true);

            this.$scope.$watch('vm.search', () => {
                if (this.selectedTab === Tab.EDIT_SEARCH) {
                    this.fabActive = this.activateActionHeader();
                }
            }, true);
        }
        
        /**
         * Configura a tabela de resultados
         */
        private configureDatatables(): void {
            this.tableOptions = {
               dom: '<"top"f>rt<"bottom"<"left"<"length"l>><"right"<"info"i><"pagination"p>>>',
               pagingType: 'full_numbers',
               autoWidth: true,
               responsive: false,
               searching: false,
               language: {
                   "sEmptyTable": "Nenhum registro encontrado",
                   "sInfo": "Mostrando de _START_ até _END_ de _TOTAL_ registros",
                   "sInfoEmpty": "Mostrando 0 até 0 de 0 registros",
                   "sInfoFiltered": "(Filtrados de _MAX_ registros)",
                   "sInfoPostFix": "",
                   "sInfoThousands": ".",
                   "sLengthMenu": "_MENU_ resultados por página",
                   "sLoadingRecords": "Carregando...",
                   "sProcessing": "Processando...",
                   "sZeroRecords": "Nenhum registro encontrado",
                   "sSearch": "Pesquisar",
                   "oPaginate": {
                       "sNext": "<i class='icon-chevron-right s10' />",
                       "sPrevious": "<i class='icon-chevron-left s10' />",
                       "sFirst": "<i class='icon-chevron-double-left s10' />",
                       "sLast": "<i class='icon-chevron-double-right s10' />"
                   },
                   "oAria": {
                       "sSortAscending": ": Ordenar colunas de forma ascendente",
                       "sSortDescending": ": Ordenar colunas de forma descendente"
                   }
               }
            };
       }
       
       /**
        * Carrega as pesquisa salvas
        */
       private loadSavedSearchs(): void {
           this.setLoadingProgress(true);
           this.pesquisaAvancadaService.loadSavedSearchs(this.searchConfig.context)
               .then(searchs => this.savedSearchs = searchs)
               .catch(() => this.messagesService.error(this.$translate.instant("PESQUISA-AVANCADA.PESQUISAS-NAO-CARREGADAS")))
               .finally(() => this.setLoadingProgress(false));
       }
       
       /**
        * Ativa o efeito do loading
        */
       private setLoadingProgress(loading: boolean): void {
           this.$rootScope["loadingProgress"] = loading;
       }

       /**
        * Valida uma pesquisa
        */
       private validate(search: ISearch): boolean {
           let invalidCriteria: string[] = [];
           let repeatedTrait: string[] = [];

           if (search.criteria.length > 1) {
                search.criteria.forEach((criterion, criterionIndex) => {
                    if (repeatedTrait.indexOf(criterion.trait.id) === -1) {
                        let countMusts = 0, countShoulds = 0;

                        search.criteria.forEach((value, valueIndex) => {
                            if (value.trait.id === criterion.trait.id && criterionIndex !== valueIndex) {
                                if (value.logicalOperator === LogicalOperator.MUST) countMusts++;
                                if (value.logicalOperator === LogicalOperator.SHOULD) countShoulds++;
                            }
                        });
                        if ((criterion.logicalOperator === LogicalOperator.MUST && (countShoulds > 0 || countMusts > 0)) ||
                            (criterion.logicalOperator === LogicalOperator.SHOULD && countMusts > 0)) {
                            invalidCriteria.push(criterion.trait.name);
                            repeatedTrait.push(criterion.trait.id);
                        }
                    }
                });
                if (invalidCriteria.length > 0) {
                    let message : string = "";
                    invalidCriteria.forEach((invalidCriterion, index) => message += `"${invalidCriterion}"`+ ((index !== (invalidCriteria.length - 1)) ? `, ` : `.`));
                    this.messagesService.warn(`Critérios combinados incorretamente: ${message}`);
                    return false;
                }    
           } else if (search.criteria.length === 1 && search.criteria[0].logicalOperator == LogicalOperator.SHOULD) {
               this.messagesService.warn(`Adicione mais um critério com o tipo "deve" ou "pode"!`);
               return false;
               
           } else if (search.criteria.length === 0) {
               return false;
           }
           return true;
       }
       
    }
    
    angular
        .module('app.pesquisa-avancada')
        .controller('app.pesquisa-avancada.PesquisaAvancadaController', PesquisaAvancadaController);
}